import React from "react";
import { Box } from "@mui/material";
import { getCountryCallingCode, CountryCode } from "libphonenumber-js";
import CustomTextField from "./common/CustomTextField";
import CustomSelect from "./common/CustomSelect";
import { COLORS } from "../assets/theme";
import { countries } from "../utils/countries";

interface Country {
  code: string;
  phone: string;
}

interface CountryCodePhoneFieldProps {
  countries: ReadonlyArray<Country>;
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  error?: boolean;
}
const validCountries = countries
  .map((country) => {
    try {
      const callingCode = getCountryCallingCode(country.code as CountryCode);
      return {
        label: `${country.code} (+${callingCode})`,
        value: country.code,
        callingCode,
        countryName: country.label,
      };
    } catch (err) {
      console.warn(`Invalid country code: ${country.code}`);
      return null;
    }
  })
  .filter(Boolean) as Array<{
  label: string;
  value: string;
  callingCode: string;
  countryName: string;
}>;

const CountryCodePhoneField: React.FC<CountryCodePhoneFieldProps> = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const filteredCountries = validCountries.filter(
    (country) =>
      country.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.countryName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          backgroundColor: "#fff",
          width: "100%",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <CustomSelect
          label="Mobile No."
          options={filteredCountries}
          bgColor={COLORS.WhiteBackground}
          value={countryCode}
          onChange={(newVal) => onCountryCodeChange(String(newVal))}
          placeholder="Code"
          highlightLabelStar
          textable
          searchable
          onSearchValueChange={(value: string) => setSearchTerm(value)}
          searchValue={searchTerm}
          inSideBorderRadius={typeof window !== "undefined" && window.innerWidth < 600 ? "12px" : "12px 0 0 12px"}
          sx={{
            minWidth: { xs: "100%", sm: "170px" },
            borderRadius: { xs: "8px 0 0 8px", sm: "8px 0 0 8px" },
            marginTop: { xs: 0, sm: 0 },
          }}
        />

        <CustomTextField
          label=""
          value={phoneNumber}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          placeholder="Enter Phone Number"
          bgColor={COLORS.WhiteBackground}
          error={!!error}
          sx={{
            marginTop: { xs: 0, sm: "24px" },
            width: { xs: "100%" },
            borderRadius: { xs: "0 8px 8px 0", sm: "0 8px 8px 0" },
            "& .MuiOutlinedInput-root": {
              borderRadius: { xs: "0 8px 8px 0", sm: "0 8px 8px 0" },
            },
            "& .MuiInputLabel-root": {
              visibility: "hidden",
              height: "0",
            },
          }}
        />
      </Box>

      {error && (
        <Box sx={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          Invalid phone number
        </Box>
      )}
    </Box>
  );
};

export default CountryCodePhoneField;
